(() => {
  const parseJson = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('PlugStore85: invalid JSON payload', error);
      return null;
    }
  };

  const formatMoney = (cents, format) => {
    const value = Number(cents || 0);
    const moneyFormat = format || '${{amount}}';
    const placeholderMatch = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts.join(decimal);
    };

    let formatted;
    switch (placeholder) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2);
    }

    return moneyFormat.replace(placeholderMatch ? placeholderMatch[0] : '{{amount}}', formatted);
  };

  function initNavigation(root) {
    const button = root.querySelector('[data-psi-menu-button]');
    const menu = root.querySelector('[data-psi-mobile-menu]');
    if (!button || !menu || button.dataset.bound) return;

    button.dataset.bound = 'true';
    const close = () => {
      menu.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
    };

    button.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      button.setAttribute('aria-expanded', String(open));
    });

    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  }

  function initSort(root) {
    root.querySelectorAll('[data-psi-sort]').forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.assign(url.toString());
      });
    });
  }

  function initFilters(root) {
    const openButton = root.querySelector('[data-psi-filter-open]');
    const panel = root.querySelector('[data-psi-filter-panel]');
    const closeButtons = root.querySelectorAll('[data-psi-filter-close]');
    if (!openButton || !panel || openButton.dataset.bound) return;

    openButton.dataset.bound = 'true';
    const close = () => document.body.classList.remove('psi-filter-open');
    openButton.addEventListener('click', () => document.body.classList.add('psi-filter-open'));
    closeButtons.forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  }

  function initGallery(root) {
    root.querySelectorAll('[data-psi-gallery]').forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = 'true';

      const panels = gallery.querySelectorAll('[data-psi-media-panel]');
      const thumbs = gallery.querySelectorAll('[data-psi-media-thumb]');
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const mediaId = thumb.dataset.psiMediaThumb;
          panels.forEach((panel) => panel.classList.toggle('is-hidden', panel.dataset.psiMediaPanel !== mediaId));
          thumbs.forEach((item) => item.classList.toggle('is-active', item === thumb));
        });
      });
    });
  }

  function initProduct(root) {
    const productRoot = root.querySelector('[data-psi-product-root]');
    if (!productRoot || productRoot.dataset.bound) return;

    const productJson = parseJson(document.querySelector('[data-psi-product-json]'));
    const moneyFormat = parseJson(document.querySelector('[data-psi-money-format]'));
    const form = productRoot.querySelector('.psi-product-form');
    if (!productJson || !form) return;

    productRoot.dataset.bound = 'true';
    const hiddenVariant = form.querySelector('[data-psi-variant-id]');
    const currentPrice = productRoot.querySelector('[data-psi-price-current]');
    const comparePrice = productRoot.querySelector('[data-psi-price-compare]');
    const submit = form.querySelector('[data-psi-product-submit]');
    const submitText = form.querySelector('[data-psi-product-submit-text]');
    const stockDot = productRoot.querySelector('[data-psi-stock-dot]');
    const stockText = productRoot.querySelector('[data-psi-stock-text]');
    const optionInputs = form.querySelectorAll('[data-psi-option-input]');

    const updateVariant = () => {
      const selectedOptions = Array.from(form.querySelectorAll('[data-psi-option-group]')).map((group) => {
        const checked = group.querySelector('[data-psi-option-input]:checked');
        return checked ? checked.value : null;
      });

      const variant = productJson.variants.find((candidate) =>
        candidate.options.every((option, index) => option === selectedOptions[index])
      );

      if (!variant) {
        hiddenVariant.value = '';
        submit.disabled = true;
        submitText.textContent = 'Unavailable';
        stockDot.classList.add('is-out');
        stockText.textContent = 'This option combination is unavailable';
        return;
      }

      hiddenVariant.value = variant.id;
      currentPrice.innerHTML = formatMoney(variant.price, moneyFormat);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePrice.innerHTML = formatMoney(variant.compare_at_price, moneyFormat);
        comparePrice.classList.remove('is-hidden');
      } else {
        comparePrice.classList.add('is-hidden');
      }

      submit.disabled = !variant.available;
      submitText.textContent = variant.available ? 'Add to cart' : 'Sold out';
      stockDot.classList.toggle('is-out', !variant.available);
      stockText.textContent = variant.available ? 'In stock and ready to order' : 'Currently unavailable';

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (variant.featured_media) {
        const thumb = productRoot.querySelector(`[data-psi-media-thumb="${variant.featured_media.id}"]`);
        if (thumb) thumb.click();
      }
    };

    optionInputs.forEach((input) => input.addEventListener('change', updateVariant));
  }

  function initPlugstoreInner(root = document) {
    document.body.classList.add('ps-inner-page-active');
    initNavigation(root);
    initSort(root);
    initFilters(root);
    initGallery(root);
    initProduct(root);
  }

  document.addEventListener('DOMContentLoaded', () => initPlugstoreInner(document));
  document.addEventListener('shopify:section:load', (event) => initPlugstoreInner(event.target));
})();
